import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import KivoBall from './KivoBall';

type ProcessingStatus = 'queued' | 'processing' | 'done' | 'error';

interface ProcessingLoaderProps {
  status: ProcessingStatus;
  queuePos: number | null;
  onCancel: () => void;
  title?: string;
  subtitle?: string;
  insights?: string[];
  slowProgress?: boolean;
}

const INSIGHTS = [
  "Identifying 14+ visible appearance patterns…",
  "Reviewing pore visibility and texture…",
  "Looking at how hydrated skin appears…",
  "Comparing against a diverse appearance dataset…",
  "Generating personalized recommendations…",
];

const EASE: [number, number, number, number] = [0.22, 0.61, 0.36, 1];

export default function ProcessingLoader({
  status,
  queuePos,
  onCancel,
  title = "reading your skin…",
  subtitle = "Our AI engine is processing your image. This usually takes 15–20 seconds.",
  insights,
  slowProgress = false,
}: ProcessingLoaderProps) {
  const activeInsights = insights && insights.length > 0 ? insights : INSIGHTS;
  const [progress, setProgress] = useState(0);
  const [insightIndex, setInsightIndex] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // On short / narrow viewports (phones) tighten every dimension so the whole
  // card fits the screen without scrolling — an oversaturated loader scrolls.
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 480px), (max-height: 740px)');
    const apply = () => { setCompact(mq.matches); };
    apply();
    mq.addEventListener('change', apply);
    return () => { mq.removeEventListener('change', apply); };
  }, []);

  const orbSize = compact ? 72 : 96;
  const ringSize = compact ? 124 : 156;
  const insightsH = compact ? 56 : 72;

  const cap = slowProgress ? 99 : 98;
  const increment = slowProgress ? 0.35 : 2;

  useEffect(() => {
    if (status === 'processing') {
      intervalRef.current = setInterval(() => {
        setProgress((prev) => prev >= cap ? prev : Math.min(cap, prev + Math.random() * increment));
      }, 300);
    } else if (status === 'done') {
      setProgress(100);
      if (intervalRef.current) clearInterval(intervalRef.current);
    } else if (status === 'queued') {
      setProgress(5);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [status, cap, increment]);

  useEffect(() => {
    const t = setInterval(() => { setInsightIndex((i) => (i + 1) % activeInsights.length); }, 3500);
    return () => { clearInterval(t); };
  }, [activeInsights.length]);

  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const isQueued = status === 'queued';
  const isError  = status === 'error';
  const orbState = isQueued ? 'thinking' : status === 'processing' ? 'streaming' : 'done';

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      maxWidth: 520,
      background: 'var(--c-cream)',
      borderRadius: 'var(--r-xl)',
      border: '1px solid var(--rule)',
      boxShadow: 'var(--sh-lifted)',
      padding: compact ? 'var(--s-5) var(--s-4)' : 'var(--s-8) var(--s-6) var(--s-7)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      overflow: 'hidden',
    }}>
      {/* Ambient blobs */}
      <div aria-hidden style={{
        position: 'absolute', top: '-15%', left: '-15%',
        width: '60%', height: '60%', borderRadius: '50%', pointerEvents: 'none',
        background: 'radial-gradient(circle, color-mix(in srgb, var(--c-kivo-teal) 18%, transparent), transparent 70%)',
        filter: 'blur(40px)',
      }} />
      <div aria-hidden style={{
        position: 'absolute', bottom: '-15%', right: '-12%',
        width: '55%', height: '55%', borderRadius: '50%', pointerEvents: 'none',
        background: 'radial-gradient(circle, color-mix(in srgb, var(--c-coral) 14%, transparent), transparent 70%)',
        filter: 'blur(40px)',
      }} />

      {/* KivoBall — living state indicator */}
      <div style={{ position: 'relative', zIndex: 1, marginBottom: compact ? 'var(--s-3)' : 'var(--s-5)' }}>
        <KivoBall size={orbSize} state={orbState} />
      </div>

      {/* Heading */}
      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.52, ease: EASE }}
        style={{
          position: 'relative', zIndex: 1,
          fontFamily: 'var(--f-display)',
          fontWeight: 400,
          fontSize: 'clamp(26px, 4vw, 36px)',
          lineHeight: 'var(--leading-display)',
          letterSpacing: 'var(--tracking-display)',
          color: 'var(--c-charcoal)',
          margin: compact ? '0 0 var(--s-2)' : '0 0 var(--s-3)',
        }}
      >
        {isQueued ? 'waiting in queue…' : title}
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.52, delay: 0.07, ease: EASE }}
        style={{
          position: 'relative', zIndex: 1,
          fontFamily: 'var(--f-text)',
          fontSize: 'var(--fs-body)',
          lineHeight: 'var(--leading-body)',
          color: 'var(--c-charcoal-60)',
          maxWidth: '40ch',
          margin: compact ? '0 auto var(--s-4)' : '0 auto var(--s-7)',
        }}
      >
        {isQueued && queuePos ? (
          <>
            You are{' '}
            <strong style={{ color: 'var(--c-deep-teal)', fontWeight: 600 }}>
              #{queuePos}
            </strong>{' '}
            in line. {subtitle}
          </>
        ) : subtitle}
      </motion.p>

      {/* Progress ring */}
      <div style={{
        position: 'relative', zIndex: 1,
        width: ringSize, height: ringSize,
        marginBottom: compact ? 'var(--s-4)' : 'var(--s-7)',
      }}>
        {/* Outer pulse ring */}
        <motion.div
          aria-hidden
          style={{
            position: 'absolute', inset: -8,
            borderRadius: '50%',
            border: '1px solid',
            borderColor: isError
              ? 'color-mix(in srgb, var(--c-coral) 30%, transparent)'
              : 'color-mix(in srgb, var(--c-kivo-teal) 20%, transparent)',
          }}
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        />

        <svg style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }} viewBox="0 0 100 100">
          <defs>
            <linearGradient id="kivo-ring-grad" gradientUnits="userSpaceOnUse" x1="50" y1="6" x2="50" y2="94">
              <stop offset="0%" stopColor="#2AA4A6" />
              <stop offset="100%" stopColor="#1A6F70" />
            </linearGradient>
            <filter id="kivo-ring-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="0" stdDeviation="2.5" floodColor="#2AA4A6" floodOpacity="0.4" />
            </filter>
          </defs>
          {/* Track */}
          <circle
            cx="50" cy="50" r={radius}
            fill="none"
            stroke="rgba(31,36,33,.08)"
            strokeWidth="5"
          />
          {/* Arc */}
          <motion.circle
            cx="50" cy="50" r={radius}
            fill="none"
            stroke={isError ? 'var(--c-coral)' : 'url(#kivo-ring-grad)'}
            strokeWidth="5"
            strokeLinecap="round"
            filter={isError ? undefined : 'url(#kivo-ring-glow)'}
            style={{ strokeDasharray: circumference }}
            animate={{ strokeDashoffset }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          />
        </svg>

        {/* Center: % + status label */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 4,
        }}>
          <motion.span
            key={Math.floor(progress)}
            initial={{ opacity: 0.5, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            style={{
              fontFamily: 'var(--f-display)',
              fontWeight: 400,
              fontSize: 38,
              lineHeight: 1,
              letterSpacing: 'var(--tracking-display)',
              color: 'var(--c-charcoal)',
            }}
          >
            {Math.round(progress)}%
          </motion.span>
          <span style={{
            fontFamily: 'var(--f-text)',
            fontWeight: 500,
            fontSize: 10,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: isError
              ? 'var(--c-coral)'
              : isQueued
                ? 'var(--c-charcoal-60)'
                : 'var(--c-kivo-teal)',
          }}>
            {isError ? 'error' : isQueued ? 'queued' : 'analyzing'}
          </span>
        </div>
      </div>

      {/* Insight carousel */}
      <div style={{
        position: 'relative', zIndex: 1,
        width: '100%', maxWidth: 360,
        height: insightsH, overflow: 'hidden',
        marginBottom: compact ? 'var(--s-3)' : 'var(--s-6)',
      }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={insightIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.38, ease: EASE }}
            style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 'var(--s-2)',
            }}
          >
            <span className="pill pill--teal" style={{ fontSize: 10, gap: 6 }}>
              <span className="dot" />
              kivo insight
            </span>
            <p style={{
              fontFamily: 'var(--f-text)',
              fontSize: 'var(--fs-body)',
              lineHeight: 'var(--leading-body)',
              color: 'var(--c-charcoal)',
              margin: 0,
            }}>
              {activeInsights[insightIndex % activeInsights.length]}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Cancel */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <button onClick={onCancel} className="btn btn--ghost btn--sm">
          Cancel analysis
        </button>
      </div>
    </div>
  );
}
